/**
 * Synced people (product interplay HF2, 2026-10-26): pure helpers shared by the screens. No React.
 *
 * - Off unless the build says exactly "true" (`VITE_INTERPLAY_PEOPLE_ENABLED`); the server's
 *   INTERPLAY_PEOPLE_ENABLED gates the API too (its routes answer 404 while off).
 * - Another product is named only by Console discovery (the shell's launcher); if discovery has no
 *   entry for the owner's product code, the words say "a connected product" — never a name in code.
 */
import type { TFunction } from 'i18next';
import { httpsUrl, initials, productBrand } from '@/interplay/etBarModel';
import type { InterplayShell } from '@/interplay/types';
import type {
  ExitMode, HostOwner, InterplayErrorInfo, InviteBatch, PersonInvite, ProposeChanges, QueueItem,
} from '@/api/endpoints/interplayPeople';

export function interplayPeopleEnabled(): boolean {
  return import.meta.env.VITE_INTERPLAY_PEOPLE_ENABLED === 'true';
}

export const peopleKeys = {
  all: ['interplay-people'] as const,
  ownership: (employeeId: string) => ['interplay-people', 'ownership', employeeId] as const,
  myOwnership: ['interplay-people', 'ownership', 'me'] as const,
  queue: (view: string, status: string, page: number) => ['interplay-people', 'queue', view, status, page] as const,
  invites: (employeeId: string) => ['interplay-people', 'invites', employeeId] as const,
  hosts: (companyId: string | null) => ['interplay-people', 'hosts', companyId] as const,
};

// ───────────────────────────────────────────────────────────────────── host brand ──

export interface HostBrand {
  code: string;
  /** From discovery; null when discovery doesn't list the product (the words say "a connected product"). */
  name: string | null;
  logoUrl: string | null;
  monogram: string;
  colour: string | null;
}

/** Product code → its Console brand, from the shell's launcher (k3-discovery). */
export function hostBrands(shell: InterplayShell | null | undefined): Record<string, HostBrand> {
  const out: Record<string, HostBrand> = {};
  for (const it of Array.isArray(shell?.launcher) ? shell!.launcher : []) {
    if (!it || typeof it.product_code !== 'string' || typeof it.product_name !== 'string') continue;
    const name = it.product_name.trim().slice(0, 120);
    if (!name) continue;
    const pb = productBrand(it.brand);
    out[it.product_code] = {
      code: it.product_code,
      name,
      logoUrl: pb?.logoUrl ?? null,
      monogram: pb?.monogram ?? initials(name),
      colour: pb?.colour ?? null,
    };
  }
  return out;
}

export function brandFor(code: string | null | undefined, brands: Record<string, HostBrand>): HostBrand {
  const hit = code ? brands[code] : undefined;
  return hit ?? { code: code ?? '', name: null, logoUrl: null, monogram: '', colour: null };
}

/** The product name from discovery, else "a connected product" (i18n). */
export function hostName(t: TFunction, brand: HostBrand): string {
  return brand.name ?? t('interplay_people.a_connected_product');
}

/** The owners' names, de-duplicated, joined for a sentence. */
export function ownersLabel(t: TFunction, owners: HostOwner[], brands: Record<string, HostBrand>): string {
  const names = Array.from(new Set(owners.map((o) => hostName(t, brandFor(o.product_code, brands)))));
  if (names.length === 0) return t('interplay_people.a_connected_product');
  return names.join(', ');
}

export { httpsUrl };

// ─────────────────────────────────────────────────────────────────── locked fields ──

/** Host-owned employee columns (H8 ownership.HOST_COLUMNS). */
export const HOST_COLUMNS = [
  'first_name', 'middle_name', 'last_name', 'work_email', 'phone', 'avatar_url', 'department_id', 'designation',
] as const;
export type HostColumn = (typeof HOST_COLUMNS)[number];

export function isLocked(locked: string[] | undefined, column: string): boolean {
  return !!locked && locked.includes(column);
}

/** The i18n key of a host-owned column's label. */
export function fieldLabelKey(column: string): string {
  return `interplay_people.field.${column}`;
}

// ───────────────────────────────────────────────────────────────────────── errors ──

/** Codes with their own words; anything else shows the server's message or a generic line. */
const KNOWN = new Set([
  'field_owned_by_host', 'employee_owned_by_host', 'unit_not_mapped', 'nothing_changed', 'field_invalid',
  'employee_not_referenced', 'person_email_in_use', 'employee_user_privileged', 'person_email_missing',
  'employee_exited', 'staff_role_missing', 'employee_user_missing', 'employee_user_changed', 'invite_unavailable',
  'invite_not_pending', 'invite_not_found', 'proposal_not_pending', 'review_superseded',
  'matched_employee_unavailable', 'plan_limit_reached', 'provider_account_not_ready', 'action_not_allowed',
  'employee_not_found', 'company_not_selected',
]);

export function errorText(t: TFunction, e: InterplayErrorInfo, vars: Record<string, string> = {}): string {
  if (e.code && KNOWN.has(e.code)) return t(`interplay_people.error.${e.code}`, vars);
  if (e.status === 404) return t('interplay_people.error.not_available');
  if (e.message) return e.message;
  return t('interplay_people.error.generic');
}

// ────────────────────────────────────────────────────────────────────────── exits ──

const EXITED = new Set(['exited', 'terminated']);

/**
 * Q136: a confirmed host exit with a future last day leaves the person on notice (or, from a
 * status with no notice period, `exit_planned`). HR completes the exit on or after that day
 * through the normal flow. Those accepted exits are HR tasks until the person has left.
 */
export interface ExitTask {
  item: QueueItem;
  mode: ExitMode;
  lastDay: string;
}

export function exitTasks(accepted: QueueItem[]): ExitTask[] {
  const out: ExitTask[] = [];
  for (const item of accepted) {
    if (item.kind !== 'exit' || item.status !== 'accepted') continue;
    const ex = item.resolution?.outcome?.exit;
    if (!ex || !ex.last_working_day) continue;
    if (!['exit_planned', 'notice_period', 'notice_end_set'].includes(ex.mode)) continue;
    const status = item.employee?.employment_status ?? '';
    if (EXITED.has(status)) continue;
    // A notice HR rescinded since (status back to active, or anything but notice) is no task.
    if ((ex.mode === 'notice_period' || ex.mode === 'notice_end_set') && item.employee && status !== 'notice_period') continue;
    out.push({ item, mode: ex.mode, lastDay: ex.last_working_day });
  }
  return out.sort((a, b) => a.lastDay.localeCompare(b.lastDay));
}

/** Today in the browser's calendar, YYYY-MM-DD. */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * What confirming will do, so HR sees the outcome before it happens. Mirrors H8 `_confirm_exit`
 * (hr_queue.py, Q136) and `hr.services.VALID_STATUS_TRANSITIONS`:
 * - already exited / terminated → `already` (nothing changes);
 * - last day today or earlier (or none) → `now` (the normal exit runs now);
 * - already on notice → `notice_end` (the notice end moves to that day);
 * - a status that may go on notice (active, probation) → `notice` (on notice until that day);
 * - any other status (onboarding, suspended, …) → `planned` (status stays; HR completes the exit
 *   on or after that day).
 */
export type ExitPreview = 'already' | 'now' | 'notice_end' | 'notice' | 'planned';
const CAN_GO_ON_NOTICE = new Set(['active', 'probation']);

export function exitPreview(lastDay: string | null, today: string, status: string | null | undefined): ExitPreview {
  const st = status ?? '';
  if (EXITED.has(st)) return 'already';
  if (!lastDay || lastDay <= today) return 'now';
  if (st === 'notice_period') return 'notice_end';
  if (CAN_GO_ON_NOTICE.has(st)) return 'notice';
  return 'planned';
}

export const EXIT_REASON_MIN = 3;

// ──────────────────────────────────────────────────────────────────────── invites ──

/** The invite that describes the person now: the newest per host product. */
export function latestInvites(items: PersonInvite[]): PersonInvite[] {
  const seen = new Set<string>();
  const out: PersonInvite[] = [];
  for (const it of [...items].sort((a, b) => b.created_at.localeCompare(a.created_at))) {
    if (seen.has(it.host_product_code)) continue;
    seen.add(it.host_product_code);
    out.push(it);
  }
  return out;
}

export interface InviteAllProgress {
  issued: number;
  refused: number;
  remaining: number;
  done: boolean;
  /** Refusals by code (e.g. person_email_in_use: 2). */
  refusedBy: Record<string, number>;
  /** The cursor to go on from (null before the first page and when done). */
  cursor: string | null;
  /** The loop was stopped before the end (Stop, or the dialog went away). */
  stopped?: boolean;
}

export const NO_PROGRESS: InviteAllProgress = { issued: 0, refused: 0, remaining: 0, done: false, refusedBy: {}, cursor: null };

/**
 * "Invite all synced staff": the server answers at most 50 per call with `next_cursor` and
 * `remaining`; loop until the cursor is null. `onProgress` is called after every page. A thrown
 * error stops the loop (the counts so far stand: every page is its own committed batch); passing
 * the last progress as `from` goes on from its cursor with the tally carried over. `signal.aborted`
 * stops before the next call (a page already sent is still counted).
 */
export async function inviteAllLoop(
  call: (cursor: string | null) => Promise<InviteBatch>,
  onProgress: (p: InviteAllProgress) => void,
  opts: { maxPages?: number; signal?: { aborted: boolean }; from?: InviteAllProgress | null } = {},
): Promise<InviteAllProgress> {
  const from = opts.from && !opts.from.done ? opts.from : NO_PROGRESS;
  const progress: InviteAllProgress = { ...from, refusedBy: { ...from.refusedBy }, done: false, stopped: false };
  let cursor: string | null = from.cursor;
  const maxPages = opts.maxPages ?? 1000;
  for (let n = 0; n < maxPages; n++) {
    if (opts.signal?.aborted) {
      progress.stopped = true;
      onProgress({ ...progress, refusedBy: { ...progress.refusedBy } });
      break;
    }
    const page = await call(cursor);
    progress.issued += page.counts?.issued ?? 0;
    progress.refused += page.counts?.refused ?? 0;
    for (const r of page.results ?? []) {
      if (r.result === 'refused') progress.refusedBy[r.error] = (progress.refusedBy[r.error] ?? 0) + 1;
    }
    progress.remaining = Math.max(0, page.remaining ?? 0);
    cursor = page.next_cursor ?? null;
    progress.cursor = cursor;
    if (!cursor) {
      progress.done = true;
      progress.remaining = 0;
    }
    onProgress({ ...progress, refusedBy: { ...progress.refusedBy } });
    if (!cursor) break;
  }
  return progress;
}

/** Percent processed so far (0–100). */
export function progressPercent(p: InviteAllProgress): number {
  const done = p.issued + p.refused;
  const total = done + p.remaining;
  if (p.done) return 100;
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

/** "Ade Okafor", from a queue employee or the held host data. */
export function personName(item: QueueItem): string {
  const e = item.employee;
  if (e && (e.first_name || e.last_name)) return `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim();
  const n = item.held?.name;
  return `${n?.given_name ?? ''} ${n?.family_name ?? ''}`.trim();
}

// ───────────────────────────────────────────────────────────────── propose change ──

export interface ProposeCurrent {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  work_email?: string | null;
  phone?: string | null;
  designation?: string | null;
  department_id?: string | null;
}

export type ProposeFormKey = keyof ProposeCurrent;
type FormKey = ProposeFormKey;
export const PROPOSE_KEYS: FormKey[] = ['first_name', 'middle_name', 'last_name', 'work_email', 'phone', 'designation', 'department_id'];
export const PROPOSE_REQUIRED: FormKey[] = ['first_name', 'last_name'];

const norm = (v: string | null | undefined) => (v ?? '').trim();

/** The changed fields only; an emptied optional field is sent as null (clears it). */
export function changedFields(current: ProposeCurrent, form: Record<FormKey, string>): ProposeChanges {
  const out: Record<string, string | null> = {};
  for (const k of PROPOSE_KEYS) {
    const next = norm(form[k]);
    if (next === norm(current[k])) continue;
    out[k] = next === '' ? null : k === 'work_email' ? next.toLowerCase() : next;
  }
  return out as ProposeChanges;
}


// ──────────────────────────────────────────────────────────── list bulk ownership ──

export interface RosterOwnershipFields {
  id: string | number;
  designation?: string | null;
  ownership?: { referenced?: boolean; owners?: HostOwner[] } | null;
  referenced_by?: HostOwner[] | null;
}

const isOwner = (o: unknown): o is HostOwner => !!o && typeof (o as HostOwner).product_code === 'string';

/** The owners the list item states itself, or undefined when it says nothing (use the fallback). */
export function inlineOwners(row: RosterOwnershipFields): HostOwner[] | undefined {
  if (Array.isArray(row.referenced_by)) return row.referenced_by.filter(isOwner);
  if ('referenced_by' in row && row.referenced_by === null) return [];
  if (row.ownership && typeof row.ownership === 'object') {
    return row.ownership.referenced === false ? [] : (row.ownership.owners ?? []).filter(isOwner);
  }
  if ('ownership' in row && row.ownership === null) return [];
  return undefined;
}

